export const vendorsResource = {
  name: 'Merchants',
  fields: {
    id: 'ID',
    name: 'Merchant Name',
    code: 'Merchant Code',
    vendorType: 'Merchant Type',
    description: 'Description',
    contactName: 'Contact Person',
    contactEmail: 'Contact Email',
    contactPhone: 'Contact Phone',
    address: 'Address',
    allowedSites: 'Allowed Premises',
    isActive: 'Active',
    apiAllowedOperations: 'Enabled APIs',
    apiControlLimitPerMinute: 'Start and stop requests per minute',
    apiReadLimitPerMinute: 'Read requests per minute',
    apisEnabled: 'APIs enabled',
    createdAt: 'Created At',
    updatedAt: 'Updated At',
  },
  types: {
    building_owner: 'Building Owner',
    facility_manager: 'Facility Manager',
    partner: 'Partner',
    corporate: 'Corporate',
  },
  sections: {
    basicInfo: 'Basic Information',
    contactInfo: 'Contact Information',
    settings: 'Settings',
    siteAssignment: 'Premises Assignment',
    users: 'Users',
    apiKeys: 'API Keys',
    apiAccess: 'API access',
  },
  userTypes: {
    backend: 'Admin Users',
    rfid: 'RFID Users',
  },
  validation: {
    codeFormat: 'Must contain only uppercase letters, numbers, and underscores',
    integer: 'Enter a whole number',
  },
  status: {
    active: 'Active',
    inactive: 'Inactive',
  },
  list: {
    subtitle: 'Manage merchant accounts for RFID card management',
  },
  create: {
    title: 'Create Merchant',
    subtitle: 'Register a new merchant account',
    infoMessage: 'Merchants are external organizations that manage RFID cards and users for their employees or clients.',
    nameHelperText: 'The official name of the merchant organization',
    codeHelperText: 'Unique identifier for API integration (e.g., MERCHANT_ABC)',
    vendorTypeHelperText: 'Select the type of merchant organization',
    descriptionHelperText: 'Optional - additional information about this merchant',
    contactNameHelperText: 'Primary contact person for this merchant',
    contactEmailHelperText: 'Email address for merchant communications',
    contactPhoneHelperText: 'Phone number for urgent matters',
    addressHelperText: 'Merchant office or billing address',
    siteAssignmentInfo: 'Premises Assignment',
    siteAssignmentHelperText: 'After creating the merchant, you can assign premises they can manage through the merchant edit page.',
    billingInfo: 'Billing Configuration',
    billingHelperText: 'Billing information and settings can be configured after creation if needed for cost allocation.',
  },
  edit: {
    title: 'Edit Merchant',
    subtitle: 'Update merchant information',
    infoMessage: 'Manage merchant information and assign premises they can manage RFID cards and users for.',
    nameHelperText: 'The official name of the merchant organization',
    codeHelperText: 'Unique identifier cannot be changed after creation',
    vendorTypeHelperText: 'Select the type of merchant organization',
    descriptionHelperText: 'Optional - additional information about this merchant',
    contactNameHelperText: 'Primary contact person for this merchant',
    contactEmailHelperText: 'Email address for merchant communications',
    contactPhoneHelperText: 'Phone number for urgent matters',
    addressHelperText: 'Merchant office or billing address',
    siteAssignmentHelp: 'Select which premises this merchant can manage RFID cards and users for. Changes are saved automatically.',
    searchSites: 'Search and select premises...',
    noSitesAvailable: 'No premises are available for assignment',
    noUsers: 'No users assigned to this merchant. Users can be created through the Backend Users or RFID Cards sections.',
    backendUsersInfo: 'Backend users have admin access to manage RFID cards and view reports for this merchant.',
    rfidUsersInfo: 'RFID users are employees who use RFID cards for charging. They don\'t have admin access.',
    billingInfo: 'Billing Configuration',
    billingHelperText: 'Billing information and settings can be configured here for cost allocation and reporting.',
  },
  siteCount: 'premises',
  show: {
    subtitle: 'View merchant details',
  },
  apiKeys: {
    description: 'OAuth 2.0 credentials allow merchants to authenticate and use the charging API.',
    generate: 'Generate Credentials',
    generating: 'Generating...',
    copy: 'Copy Credentials',
    noKeys: 'No OAuth 2.0 credentials generated yet. Click "Generate Credentials" to create one.',
    noDescription: 'No description',
    neverExpires: 'Never',
    revokeTooltip: 'Revoke Credentials',
    loadFailed: 'Failed to load OAuth 2.0 credentials',
    generateSuccess: 'OAuth 2.0 credentials generated successfully',
    generateFailed: 'Failed to generate OAuth 2.0 credentials',
    revokeSuccess: 'OAuth 2.0 credentials revoked successfully',
    revokeFailed: 'Failed to revoke OAuth 2.0 credentials',
    clientSecretCopied: 'Client secret copied to clipboard',
    status: {
      active: 'Active',
      expired: 'Expired',
      revoked: 'Revoked',
    },
    expiration: {
      days7: '7 days',
      days30: '30 days',
      days60: '60 days',
      days90: '90 days',
      days180: '180 days',
      days365: '1 year (365 days)',
      never: 'No expiration',
      custom: 'Custom…',
    },
    fields: {
      suffix: 'Key Suffix',
      description: 'Description',
      status: 'Status',
      created: 'Created',
      lastUsed: 'Last Used',
      expires: 'Expires',
      actions: 'Actions',
      expiresInDays: 'Expires In (Days)',
      expiration: 'Expiration',
    },
    generateDialog: {
      title: 'Generate New OAuth 2.0 Credentials',
      descriptionHelp: 'Optional: Describe what these credentials will be used for',
      expiresHelp: 'Optional: Client secret will expire after this many days',
      customExpiresHelp: 'Enter a number of days (1 or more)',
      customRequired: 'Please enter a custom expiry in days, or pick another option',
      neverExpireWarning: 'Credentials that never expire should be rotated manually. Make sure you trust the recipient.',
      warning: 'The client secret will be shown only once. Make sure to copy and save it securely.',
    },
    revokeDialog: {
      title: 'Revoke OAuth 2.0 Credentials?',
      body: 'Are you sure you want to revoke these OAuth 2.0 credentials? This action cannot be undone. Any integrations using these credentials will stop working immediately.',
      confirm: 'Revoke',
      revoking: 'Revoking…',
    },
    newKeyDialog: {
      title: 'OAuth 2.0 Credentials Generated Successfully',
      important: 'IMPORTANT: This is the only time you will see the client secret!',
      saveNow: 'Copy and save it now. You will not be able to retrieve it later.',
      clientId: 'Client ID',
      clientSecret: 'Client Secret',
      copied: 'OAuth 2.0 credentials copied to clipboard!',
      usage: 'Share these OAuth 2.0 credentials with the merchant securely. They will use them to obtain bearer tokens for API authentication.',
    },
  },
  apiAccess: {
    help: 'Choose which API endpoints this account may call and how many requests per minute it may make. Getting a token is always allowed.',
    controlLimitHelperText: 'Applies to start and stop calls. 1 to 10,000.',
    readLimitHelperText: 'Applies to every GET call. 1 to 100,000.',
    selectAll: 'Select all',
    clear: 'Clear',
    enabledCount: '%{count} of %{total} enabled',
    noneEnabledWarning: 'This account cannot call any API until you enable at least one.',
    groups: {
      sites: 'Sites',
      chargers: 'Chargers',
      sessions: 'Sessions',
      reports: 'Reports',
    },
    operations: {
      sitesList: 'List sites',
      chargersList: 'List chargers',
      chargersGet: 'Get one charger',
      sessionsStart: 'Start a charging session',
      sessionsStop: 'Stop a charging session',
      sessionsList: 'List sessions',
      sessionsGet: 'Get one session',
      sessionsMeterValues: 'Read the meter values of a session',
      reportsSessions: 'Session report',
      reportsSessionsExport: 'Export sessions as CSV',
    },
  },
};

export const vendors = {
  vendors: vendorsResource,
};